import { captureException } from '@sentry/react-router'
import { useEffect, useState, type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useLocation,
	useParams,
	useRouteError,
} from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { cn, getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = (error) => <DiagnosticErrorPanel error={error} />,
	className,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
	/**
	 * Replaces the default admin-shaped shell. Customer-facing routes need
	 * their own width and type scale — the default `text-h2`/`p-20` wrapper
	 * blows body copy up past the heading on a phone.
	 */
	className?: string
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	return (
		<div
			className={cn(
				'text-h2 container flex items-center justify-center p-20',
				className,
			)}
		>
			{isResponse
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}

/**
 * Default unexpected-error panel: human-readable top line, then a
 * copy-pasteable diagnostic block (name, message, URL, timestamp, stack).
 * Replaces the previous bare `<p>{message}</p>` so a staff member can
 * forward one block to engineering instead of describing the error from
 * memory. Jacob 2026-10-24: "something with a human readable message and
 * then copy/pastable error message and logs."
 *
 * Admin-wide. Customer-facing routes already override
 * `unexpectedErrorHandler` for voice/width reasons, so their simpler
 * messaging is preserved.
 */
function DiagnosticErrorPanel({ error }: { error: unknown }) {
	const location = useLocation()
	const [copied, setCopied] = useState(false)
	const name =
		error && typeof error === 'object' && 'name' in error
			? String((error as { name: unknown }).name)
			: 'Error'
	const message = getErrorMessage(error)
	const stack =
		error && typeof error === 'object' && 'stack' in error
			? String((error as { stack: unknown }).stack ?? '')
			: ''
	// Sampled on render — the window of interest is "when did the staff
	// member see this." The error itself may be seconds older server-side,
	// but the correlation point is the viewer's timeline.
	const [when] = useState(() => new Date().toISOString())
	const url =
		typeof window !== 'undefined'
			? `${window.location.origin}${location.pathname}${location.search}`
			: `${location.pathname}${location.search}`

	const block = [
		`Error:  ${name}: ${message}`,
		`Where:  ${url}`,
		`When:   ${when}`,
		'',
		stack ? `Stack:\n${stack}` : '(no stack available)',
	].join('\n')

	async function copy() {
		try {
			await navigator.clipboard.writeText(block)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			// Clipboard permission denied — pre tag stays selectable, user can
			// select-copy manually. No point surfacing the failure.
		}
	}

	return (
		<div className="w-full max-w-3xl space-y-4 text-left">
			<div className="space-y-1">
				<h2 className="text-h2">Something went wrong</h2>
				<p className="text-body-md text-muted-foreground">
					This page failed to load. The error was logged; if you need to
					report it, copy the block below.
				</p>
			</div>
			<div className="flex items-center justify-between">
				<p className="text-body-sm font-semibold">Diagnostic</p>
				<Button type="button" size="sm" variant="outline" onClick={copy}>
					{copied ? 'Copied' : 'Copy'}
				</Button>
			</div>
			<pre className="bg-muted text-muted-foreground max-h-96 overflow-auto rounded-md p-4 text-xs leading-relaxed whitespace-pre-wrap">
				{block}
			</pre>
		</div>
	)
}
